import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Save, RefreshCw, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

const PAGES = [
  { slug: 'home', label: 'Home page' },
  { slug: 'contact', label: 'Contact page' },
];

const FAQsAdmin = () => {
  const [slug, setSlug] = useState('home');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setDirty(false);
    api.get(`/faqs/${slug}`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => toast.error('Failed to load FAQs'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [slug]);

  const update = (i, key, val) => {
    setItems(list => list.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
    setDirty(true);
  };
  const add = () => { setItems(list => [...list, { question: '', answer: '' }]); setDirty(true); };
  const remove = (i) => { setItems(list => list.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    // Basic validation.
    for (const it of items) {
      if (!it.question?.trim() || !it.answer?.trim()) {
        toast.error('Every FAQ needs both a question and an answer'); return;
      }
    }
    setSaving(true);
    try {
      await api.put(`/admin/faqs/${slug}`, { items });
      toast.success('FAQs saved');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset to the built-in defaults? Your edits will be lost.')) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/faqs/${slug}/reset`);
      setItems(r.data?.items || []);
      setDirty(false);
      toast.success('Reset to defaults');
    } catch { toast.error('Reset failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-faqs">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-[#12263A] flex items-center gap-2"><HelpCircle className="w-5 h-5 text-[#F46A25]" /> FAQ content</h2>
          <p className="text-xs text-neutral-600 mt-1">
            Edit the FAQs shown on the Home and Contact pages. They are also picked up by Google as
            <b> FAQPage rich results</b> via JSON-LD schema — so wording them well = more clicks from search.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {PAGES.map(p => (
            <button
              key={p.slug}
              onClick={() => setSlug(p.slug)}
              data-testid={`faq-page-tab-${p.slug}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-sm ${slug === p.slug ? 'bg-[#F46A25] text-white' : 'bg-neutral-100 text-[#12263A] hover:bg-neutral-200'}`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25] mx-auto" /></div>
      ) : (
        <>
          <ul className="space-y-3">
            {items.map((it, i) => (
              <li key={i} className="bg-white border border-neutral-200 rounded-sm p-3" data-testid={`admin-faq-item-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">FAQ {i + 1}</span>
                  <button
                    onClick={() => remove(i)}
                    data-testid={`admin-faq-remove-${i}`}
                    className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
                  ><Trash2 className="w-3 h-3" /> Remove</button>
                </div>
                <input
                  value={it.question}
                  onChange={e => update(i, 'question', e.target.value)}
                  placeholder="Question"
                  data-testid={`admin-faq-question-${i}`}
                  className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm font-bold mb-2 focus:outline-none focus:border-[#F46A25]"
                />
                <textarea
                  value={it.answer}
                  onChange={e => update(i, 'answer', e.target.value)}
                  rows={3}
                  placeholder="Answer (keep it clear and keyword-rich — Google reads this)"
                  data-testid={`admin-faq-answer-${i}`}
                  className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
                />
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 flex-wrap pt-2">
            <button
              onClick={add}
              data-testid="admin-faq-add-btn"
              className="text-xs font-bold border border-neutral-300 hover:border-[#F46A25] px-3 py-1.5 rounded-sm flex items-center gap-1"
            ><Plus className="w-3 h-3" /> Add FAQ</button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="admin-faq-save-btn"
              className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-xs px-4 py-2 rounded-sm disabled:opacity-60 flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save {PAGES.find(p => p.slug === slug)?.label} FAQs
            </button>
            <button
              onClick={reset}
              disabled={saving}
              data-testid="admin-faq-reset-btn"
              className="border border-neutral-300 hover:border-red-300 hover:text-red-600 text-neutral-600 font-semibold text-xs px-3 py-1.5 rounded-sm flex items-center gap-1"
            ><RefreshCw className="w-3.5 h-3.5" /> Reset to defaults</button>
          </div>
        </>
      )}
    </div>
  );
};

export default FAQsAdmin;
